import { dayLookup, payForDay, splitHoursOn, startOfWeek } from "@core";
import type { Employee, EmploymentTerms, Occupation, Shift, Site } from "@core";

import { supabase } from "./supabase";
import { loadDeclaredHolidays } from "./declared-holidays";

/**
 * What the dashboard needs about a household, read directly.
 *
 * The app has `loadEmployerContext` in src/features/employer-data, and sharing
 * it was the obvious move — but it reaches `./auth`, which reaches
 * expo-notifications and expo-linking, and none of that resolves in a browser.
 * The boundary this settles on is: **share the rules, not the plumbing.**
 * Every hour, multiplier and rand below is computed by src/core, the same code
 * the app runs and the same code src/core's tests cover. What is restated here
 * is column mapping, which is mechanical and cannot drift in a way that changes
 * an answer.
 *
 * (If a third client ever appears, the row mappers in src/features/employee-data
 * are the thing worth lifting into src/core.)
 */

export interface EmployerView {
  orgId: string;
  orgName: string;
  country: string;
  /** The household's timezone. Every clock time on the register is formatted
   *  in it, never the viewer's — a register read from another country would
   *  otherwise show times that were never recorded. */
  timezone: string;
  currency: string;
  sites: Site[];
  employees: Employee[];
  siteOf: (id: string) => Site | undefined;
  /** Somebody's current terms — the row with no end date. Absent until pay is set. */
  openTermFor: (employeeId: string) => EmploymentTerms | undefined;
  week: Map<string, PersonWeek>;
}

export interface PersonWeek {
  total: number;
  overtime: number;
  /** Hours on a public holiday. BCEA s33(2) wants them stated separately, and
   *  s18 prices them as a whole day rather than at an hourly multiplier. */
  holiday: number;
  wages: number;
  /** Still clocked in, so this week's hours are only what has been closed off. */
  onSite: boolean;
}

/** Only the columns the week's totals actually need. */
type WeekShift = Pick<Shift, "employeeId" | "termsId" | "clockIn" | "clockOut">;

/**
 * Row shapes are declared rather than inferred. supabase-js parses the select
 * string as a type-level literal, so a string built by concatenation — which
 * the employees query needs to stay readable — infers as GenericStringError and
 * every field access fails. `.returns<T[]>()` says plainly what comes back.
 */
interface EmployeeRow {
  id: string; org_id: string; site_id: string; user_id: string | null;
  full_name: string; phone: string | null; occupation: string;
  occupation_other: string | null; nationality: string; started_on: string;
  status: string; ended_on: string | null;
}

function toTerms(row: Record<string, any>): EmploymentTerms {
  return {
    id: row.id,
    employeeId: row.employee_id,
    validFrom: row.valid_from,
    validTo: row.valid_to,
    // Money is stored in cents so it is never a float in the database.
    rate: row.rate_cents / 100,
    currency: row.currency,
    ordinaryDaily: Number(row.ordinary_daily),
    ordinaryWeekly: Number(row.ordinary_weekly),
    breakMins: row.break_mins,
    breakAfter: Number(row.break_after_h),
    ot: Number(row.ot_multiplier),
    sunday: Number(row.sunday_multiplier),
    holiday: Number(row.holiday_multiplier ?? 2),
    daysPerWeek: row.days_per_week,
    annualLeaveDays: Number(row.annual_leave_days),
    sickLeaveDays: Number(row.sick_leave_days),
    sickCycleMonths: row.sick_cycle_months,
    familyLeaveDays: Number(row.family_leave_days),
  };
}

export async function loadEmployer(orgId: string): Promise<EmployerView | null> {
  const orgRes = await supabase
    .from("orgs")
    .select("id, name, country_code, timezone")
    .eq("id", orgId)
    .maybeSingle();
  if (!orgRes.data) return null;

  // Proclaimed days, handed to core before anything prices a day. Loaded here
  // rather than per screen because every screen in this app is downstream of
  // this call, and a list threaded through each of them is a list one of them
  // eventually forgets.
  await loadDeclaredHolidays(orgRes.data.country_code ?? "ZA");

  const [siteRes, employeeRes] = await Promise.all([
    supabase.from("sites").select("id, org_id, name, address, lat, lng, radius_m").eq("org_id", orgId),
    supabase
      .from("employees")
      .select(
        "id, org_id, site_id, user_id, full_name, phone, occupation, occupation_other, " +
          "nationality, started_on, status, ended_on",
      )
      .eq("org_id", orgId)
      .order("full_name")
      .returns<EmployeeRow[]>(),
  ]);

  const sites: Site[] = (siteRes.data ?? []).map((r) => ({
    id: r.id,
    orgId: r.org_id,
    name: r.name,
    address: r.address ?? "",
    lat: r.lat,
    lng: r.lng,
    radiusM: r.radius_m,
  }));

  const employees: Employee[] = (employeeRes.data ?? []).map((r) => ({
    id: r.id,
    orgId: r.org_id,
    siteId: r.site_id,
    userId: r.user_id,
    fullName: r.full_name,
    phone: r.phone ?? "",
    occupation: r.occupation as Occupation,
    occupationOther: r.occupation_other,
    nationality: r.nationality as Employee["nationality"],
    startedOn: r.started_on,
    status: r.status === "ended" ? "ended" : "active",
    endsOn: r.ended_on,
    docs: [],
  }));

  const ids = employees.map((e) => e.id);
  const since = new Date(startOfWeek(Date.now())).toISOString();

  const [termRes, shiftRes] = await Promise.all([
    ids.length ? supabase.from("employment_terms").select("*").in("employee_id", ids) : { data: [] },
    ids.length
      ? supabase
          .from("shifts")
          .select("employee_id, terms_id, clock_in, clock_out")
          .in("employee_id", ids)
          .gte("clock_in", since)
      : { data: [] },
  ]);

  const terms = new Map<string, EmploymentTerms>();
  const openTerm = new Map<string, EmploymentTerms>();
  for (const row of (termRes.data ?? []) as Record<string, any>[]) {
    const term = toTerms(row);
    terms.set(term.id, term);
    if (term.validTo === null) openTerm.set(term.employeeId, term);
  }

  const shifts: WeekShift[] = ((shiftRes.data ?? []) as Record<string, any>[]).map((r) => ({
    employeeId: r.employee_id,
    termsId: r.terms_id,
    clockIn: Date.parse(r.clock_in),
    clockOut: r.clock_out ? Date.parse(r.clock_out) : null,
  }));

  const dayOf = dayLookup();
  const week = new Map<string, PersonWeek>();
  for (const employee of employees) {
    week.set(employee.id, { total: 0, overtime: 0, holiday: 0, wages: 0, onSite: false });
  }
  for (const shift of shifts) {
    const row = week.get(shift.employeeId);
    if (!row) continue;
    if (!shift.clockOut) {
      row.onSite = true;
      continue;
    }
    // Priced against the terms the shift was worked under, never today's rate.
    // That is the whole point of effective dating, and getting it wrong
    // restates the register rather than only the money.
    const term = terms.get(shift.termsId);
    if (!term) continue;
    // splitHoursOn reads clockIn and clockOut and nothing else; WeekShift
    // carries both. The cast is narrow on purpose — widen the query, not this,
    // if that ever stops being true.
    //
    // No work pattern here either, so a public holiday is priced as a day the
    // person ordinarily works. That overpays rather than underpays, which is
    // the direction to err in.
    const day = dayOf(shift as Shift);
    const split = splitHoursOn(shift as Shift, term, day);
    row.total += split.total;
    row.overtime += split.overtime;
    row.holiday += split.holiday;
    row.wages += payForDay(shift as Shift, term, day).amount;
  }

  const siteById = new Map(sites.map((s) => [s.id, s]));

  return {
    orgId,
    orgName: orgRes.data.name,
    country: orgRes.data.country_code,
    timezone: orgRes.data.timezone ?? "Africa/Johannesburg",
    currency: (openTerm.values().next().value as EmploymentTerms | undefined)?.currency ?? "ZAR",
    sites,
    employees,
    siteOf: (id) => siteById.get(id),
    openTermFor: (employeeId) => openTerm.get(employeeId),
    week,
  };
}
